/**
 * Data that may be represented in the pagination panel
 */
export interface PaginationParams {
  /**
   * First currently show record, 1-based.
   */
  fromRecord: number;
  /**
   * Last currently show record, 1-based.
   */
  toRecord: number;
  /**
   * Current page, 1-based.
   */
  currentPage: number;
  /**
   * Number of data records, null if not available (infinite data).
   */
  totalRecords: number | null;
  /**
   * Number of pages, null if not available (infinite data).
   */
  totalPages: number | null;
  /**
   * Numer of data items per page.
   */
  recordsPerPage: number;
  /**
   * Whether the "Previous Page" and "First Page" buttons are disabled.
   */
  backDisabled: boolean;
  /**
   * Whether the "Next Page" and "Last Page" buttons are disabled.
   */
  forwardDisabled: boolean;
  /**
   * A message describing currently shown records, e. g. "1 to 10 of 100".
   */
  recordMessage?: string;
  /**
   * A message describing currently shown page, e. g. "Page 1 of 100".
   */
  pageMessage?: string;
}

/**
 * What can change in the lifecycle of the pagination.
 */
export interface PaginationState {
  currentPage: number;
  totalRecords: number | null;
  recordsPerPage: number;
}

export interface DataRequest {
  page: number;
  pageSize: number;
}

/**
 * Configurable utilities for creating PaginationState.
 */
export interface PaginationConfig {
  createRecordMessage?: (
    fromRecord: number,
    toRecord: number,
    totalRecords: number | null,
  ) => string;
  createPageMessage?: (
    currentPage: number,
    totalPages: number | null,
  ) => string;
}

function totalPagesForPaginationState(state: PaginationState): number | null {
  // Compute totalPages
  // If totalRecords is null (infinite data), this is set to null.
  // If there is no data, set to 1 (one empty page).
  // In the usual case of finite data, compute the number of pages to hold the data.
  const { totalRecords, recordsPerPage } = state;
  let totalPages: number | null = null;
  if (recordsPerPage > 0 && totalRecords !== null) {
    totalPages = Math.ceil(totalRecords / recordsPerPage);
  }
  if (totalRecords === 0) {
    totalPages = 1;
  }
  return totalPages;
}

export function updatePaginationState(
  state: PaginationState,
  update: Partial<PaginationState>,
): PaginationState {
  // Updated totalRecords
  const totalRecords =
    update.totalRecords === undefined
      ? state.totalRecords
      : update.totalRecords;

  // Updated recordsPerPage
  const recordsPerPage =
    update.recordsPerPage === undefined
      ? state.recordsPerPage
      : update.recordsPerPage;

  const totalPages = totalPagesForPaginationState(state);

  // Updated currentPage; make sure it does not exceed totalPages.
  let currentPage =
    update.currentPage === undefined ? state.currentPage : update.currentPage;
  if (totalPages !== null && currentPage > totalPages) {
    currentPage = totalPages;
  }

  return { currentPage, totalRecords, recordsPerPage };
}

export function paginationStateToParams(
  state: PaginationState,
  config?: PaginationConfig,
): PaginationParams {
  // Updated totalRecords
  const { totalRecords, recordsPerPage, currentPage } = state;
  const totalPages = totalPagesForPaginationState(state);

  // Buttons are disabled if there are no pages to move to.
  const backDisabled = currentPage === 1;
  const forwardDisabled = currentPage === totalPages;

  // Updated fromRecord and toRecord: compute the indices of the first and last item on the current page.
  let fromRecord = 0;
  let toRecord = 0;
  if (totalRecords === null || totalRecords > 0) {
    fromRecord = (currentPage - 1) * recordsPerPage + 1;
    toRecord = Math.min(currentPage * recordsPerPage); // works if the page is full
  }
  if (totalRecords !== null && toRecord > totalRecords) {
    toRecord = totalRecords; // works if the last page is not full
  }

  // Compute user messages using configurable functions.
  const recordMessage = config?.createRecordMessage?.(
    fromRecord,
    toRecord,
    totalRecords,
  );
  const pageMessage = config?.createPageMessage?.(currentPage, totalPages);
  return {
    currentPage,
    totalPages,
    totalRecords,
    recordsPerPage,
    backDisabled,
    forwardDisabled,
    fromRecord,
    toRecord,
    recordMessage,
    pageMessage,
  };
}

export type ColumnFilter = { colId: string; filter: string }[];

export enum SortDirection {
  none = 'none',
  asc = 'asc',
  desc = 'desc',
}

export type GridSort =
  | {
      sortDirection: SortDirection.none;
    }
  | {
      sortDirection: SortDirection.asc | SortDirection.desc;
      sortBy: string;
    };

const trivialSort: GridSort = { sortDirection: SortDirection.none };

export const GridSort = {
  none: trivialSort,
  asc: (sortBy: string): GridSort => ({
    sortDirection: SortDirection.asc,
    sortBy,
  }),
  desc: (sortBy: string): GridSort => ({
    sortDirection: SortDirection.desc,
    sortBy,
  }),
  of: ({
    sortDirection,
    sortBy,
  }: {
    sortDirection: string | null | undefined;
    sortBy: string | null | undefined;
  }): GridSort => {
    if (!sortDirection || sortBy === undefined || sortBy === null) {
      return trivialSort;
    }
    if (
      sortDirection === SortDirection.asc ||
      sortDirection === SortDirection.desc
    ) {
      return { sortDirection: sortDirection, sortBy };
    }
    return trivialSort;
  },
  eq: (sort1: GridSort, sort2: GridSort): boolean => {
    if (
      sort1.sortDirection === SortDirection.none &&
      sort2.sortDirection === SortDirection.none
    ) {
      return true;
    }
    if (
      sort1.sortDirection !== SortDirection.none &&
      sort2.sortDirection !== SortDirection.none
    ) {
      return (
        sort1.sortDirection === sort2.sortDirection &&
        sort1.sortBy === sort2.sortBy
      );
    }
    return false;
  },
};
